import './LandingPage.scss'

//@ts-ignore
import { HeaderData } from '@componentsfld/core/HeaderNav'
//@ts-ignore
import Layout from '@componentsfld/core/Layout'
import { HeadProps } from 'gatsby'
import React, { FunctionComponent } from 'react'
import {
  GenericPageProps,
  IPuxLandingPage,
} from 'Root/Shared/queries/page-queries'
import { IPageContext } from 'Root/Shared/utils/data/buildTypedPage'
import { getFaviconUrls } from 'Root/Shared/utils/favicon'
import { getLocalizedData, getLocalizedUrl } from 'Root/Shared/utils/localeURL'
import PuxMetaTagsGatsby from 'Root/Shared/utils/PuxMetaTagsGatsby'
import { PuxVWO } from 'Root/Shared/utils/PuxVWO'
import WidgetBuilder from 'Shared/components/builders/WidgetBuilder'
import { menuItemsMapper } from 'Shared/utils/menuItemsMapper'

const rootClass = `landing-page`

const localizedConfigData = getLocalizedData(
  process.env.LOCALE ?? ``,
  process.env.PROJECT ?? ``
)
const activeEnv = process.env.GATSBY_ACTIVE_ENV ?? ``

function getLandingPageStructuredData(
  pageData: IPuxLandingPage,
  metaTags: any,
  breadcrumbItems?: { displayText: string; path: string }[]
) {
  const baseUrl = localizedConfigData.domains[activeEnv]
  const currentPath = getLocalizedUrl(pageData.path) || metaTags.metaTagsCanonicalUrl?.html || ''
  const fullUrl = `${baseUrl}${currentPath}`
  const websiteUrl = `${baseUrl}/#website`
  const organizationUrl = `${baseUrl}/#organization`
  const logoUrl = `${baseUrl}/#logo`
  const breadcrumbUrl = `${fullUrl}#breadcrumb`
  const webpageUrl = `${fullUrl}#webpage`

  const productName = "Easy8"
  const brandUrl = baseUrl
  const logoFullUrl = getFaviconUrls({
    baseUrl: process.env.GATSBY_FAVICON_URL,
    version: process.env.GATSBY_FAVICON_VERSION,
  }).favicon96

  const description = metaTags.metaTagsDescription?.html || metaTags.metaTagsOGDescription?.html || ''
  const title = metaTags.metaTagsTitle?.html || metaTags.metaTagsOGTitle?.html || pageData.displayText
  const cleanDescription = description.replace(/<[^>]*>/g, '')
  const cleanTitle = title.replace(/<[^>]*>/g, '')

  const socialLinks = [
        "https://easy8group.medium.com/",
        "https://www.youtube.com/@Easy8Group",
        "https://substack.com/@easy8group",
        "https://www.facebook.com/easy8Group/",
        "https://www.linkedin.com/company/easy8/",
        "https://x.com/Easy8Group"
      ]

  const graph: any[] = []

  // Product
  graph.push({
    "@type": "Product",
    "name": productName,
    "url": fullUrl,
    "image": logoFullUrl,
    "description": cleanDescription,
    "brand": {
      "@type": "Brand",
      "name": productName,
      "url": brandUrl
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.5",
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": "109"
    }
  })

  // Organization
  graph.push({
    "@type": "Organization",
    "@id": organizationUrl,
    "name": "Easy Software",
    "url": baseUrl,
    "logo": {
      "@type": "ImageObject",
      "@id": logoUrl,
      "url": logoFullUrl,
      "contentUrl": logoFullUrl,
      "caption": productName,
      "inLanguage": localizedConfigData.isoCode || "en"
    },
    "sameAs": socialLinks
  })

  // WebSite
  graph.push({
    "@type": "WebSite",
    "@id": websiteUrl,
    "url": baseUrl,
    "name": productName,
    "publisher": {
      "@id": organizationUrl
    },
    "inLanguage": localizedConfigData.isoCode || "en"
  })

  // BreadcrumbList
  if (breadcrumbItems && breadcrumbItems.length > 0) {
    const breadcrumbListElements = breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@id": `${baseUrl}${item.path}`,
        "name": item.displayText
      }
    }))

    graph.push({
      "@type": "BreadcrumbList",
      "@id": breadcrumbUrl,
      "itemListElement": breadcrumbListElements
    })
  } else {
    // Default breadcrumb with Home and current page
    graph.push({
      "@type": "BreadcrumbList",
      "@id": breadcrumbUrl,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@id": baseUrl,
            "name": "Home"
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@id": fullUrl,
            "name": pageData.displayText
          }
        }
      ]
    })
  }

  // WebPage
  graph.push({
    "@type": "WebPage",
    "@id": webpageUrl,
    "url": fullUrl,
    "name": cleanTitle,
    "description": cleanDescription,
    "isPartOf": {
      "@id": websiteUrl
    },
    "inLanguage": localizedConfigData.isoCode || "en",
    "breadcrumb": {
      "@id": breadcrumbUrl
    }
  })

  return {
    "@context": "https://schema.org",
    "@graph": graph
  }
}

const LandingPage: FunctionComponent<GenericPageProps<IPuxLandingPage>> = (
  props
) => {
  const pageData = props.pageContext.pageData
  const widgetZone = props.pageContext.widgets?.landingWidgets
  const headerData = props.pageContext.headerData
  const footerData = props.pageContext.footerData
  const metaTags = props.pageContext.metaTags
  const breadcrumbData = props.pageContext.breadcrumbs

  const landingHeaderData: HeaderData = {
    menuItems: menuItemsMapper(pageData?.landingPageAnchors?.contentItems),
  }
  const editorPath = `landingWidgets,widgets`

  const structuredData = getLandingPageStructuredData(
    pageData,
    metaTags,
    breadcrumbData
  )

  return (
    <Layout
      localizedPath={pageData.localization?.localizations}
      style='landing'
      cta={pageData?.landingPageButton}
      footerData={footerData}
      headerData={headerData}
      landingPageNav={landingHeaderData}
      landingPageLogo={pageData.landingPageLogo}
      pageContentItemId={pageData.contentItemId}
      pageProperties={props.pageContext.pageProperties}
      resStrings={props.pageContext.resStrings}
    >
      <div className={rootClass}>
        <div className={`${rootClass}-widgets`}>
          <WidgetBuilder
            pageContentItemId={pageData.contentItemId}
            widgetBuilderData={widgetZone}
            editorPath={editorPath}
          />
        </div>
      </div>
    </Layout>
  )
}

export default LandingPage

export function Head(props: HeadProps<never, IPageContext<IPuxLandingPage>>) {
  const pageData = props.pageContext.pageData
  const metaTags = props.pageContext.metaTags
  const breadcrumbData = props.pageContext.breadcrumbs

  return (
    <>
      <PuxMetaTagsGatsby
        {...metaTags}
        localizedPaths={pageData.localization?.localizations}
        structuredData={getLandingPageStructuredData(
          pageData,
          metaTags,
          breadcrumbData
        )}
      />
      <PuxVWO isEnabled={props.pageContext.pageProperties?.pagePropertiesVWOEnabled} />
    </>
  )
}
